import { useBreakpointDown } from '@all-manager/design-system-v2';
import { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import { useMemo, useState } from 'react';
import { ExpenseCategoryDatabase, ExpenseDatabase } from '../../../../types';
import { getSeriesColor } from '../../utils/chartTheme';
import { getCategoryTotals, getExpenseYears, getMonthlyAmounts, MONTH_LABELS } from '../../utils/expenseStats';
import { formatAmount } from '../../utils/formatAmount';

type Props = {
  categories: ExpenseCategoryDatabase[];
  expenses: ExpenseDatabase[];
};

const MIN_COMPARED_CATEGORIES = 2;

export function useCategoryComparison(props: Props) {
  const currentYear = new Date().getFullYear();
  const isCompact = useBreakpointDown('md');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const selectedCategories = props.categories.filter((category) => selectedIds.includes(category.id));
  const canCompare = selectedCategories.length >= MIN_COMPARED_CATEGORIES;

  const comparedExpenses = useMemo(
    () => props.expenses.filter((expense) => selectedIds.includes(expense.category_id)),
    [props.expenses, selectedIds],
  );
  const years = useMemo(() => getExpenseYears(comparedExpenses, currentYear), [comparedExpenses, currentYear]);
  const totals = getCategoryTotals(comparedExpenses, selectedCategories, selectedYear);

  const chartData: ChartData<'line', (number | null)[], string> = {
    labels: MONTH_LABELS,
    datasets: selectedCategories.map((category, index) => ({
      label: category.name,
      data: getMonthlyAmounts(comparedExpenses, category.id, selectedYear).map((amount) => amount ?? 0),
      borderColor: getSeriesColor(index),
      backgroundColor: getSeriesColor(index),
      tension: 0,
    })),
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    aspectRatio: isCompact ? 1.2 : 2,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<'line'>) => (
            `${context.dataset.label} : ${typeof context.raw === 'number' ? formatAmount(context.raw) : '—'}`
          ),
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => (typeof value === 'number' ? formatAmount(value) : value),
        },
      },
    },
  };

  function handleToggleCategory(categoryId: string) {
    setSelectedIds((current) => (
      current.includes(categoryId) ? current.filter((id) => id !== categoryId) : [...current, categoryId]
    ));
  }

  return {
    selectedIds,
    hasEnoughCategories: props.categories.length >= MIN_COMPARED_CATEGORIES,
    canCompare,
    missingCount: MIN_COMPARED_CATEGORIES - selectedCategories.length,
    years,
    selectedYear,
    totals,
    chartData,
    chartOptions,
    handleToggleCategory,
    handleYearChange: setSelectedYear,
  };
}
